import React, { useState } from 'react';
import { ChevronRight, Paperclip, RotateCcw, FileText, Music, Download } from 'lucide-react';

interface ScheduleBookFileProps {
    fileSets: any[];
}

const ScheduleBookFile: React.FC<ScheduleBookFileProps> = ({ fileSets }) => {
    const [selectedFileSetId, setSelectedFileSetId] = useState<string | null>(null);

    const formatSize = (bytes: number) => {
        if (!bytes || bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <div className="space-y-3">
            {!selectedFileSetId ? (
                fileSets.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {fileSets.map((set, idx) => (
                            <button
                                key={idx}
                                onClick={() => setSelectedFileSetId(set.id)}
                                className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-transparent hover:border-emerald-200 hover:bg-white transition-all text-left group"
                            >
                                <div className="flex flex-col">
                                    <h5 className="text-xs font-black text-slate-800">{set.title}</h5>
                                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{(set.files || []).length} 자료</span>
                                </div>
                                <div className="w-8 h-8 rounded-lg bg-white shadow-sm flex items-center justify-center text-emerald-500 group-hover:bg-emerald-600 group-hover:text-white transition-all">
                                    <ChevronRight size={16} />
                                </div>
                            </button>
                        ))}
                    </div>
                ) : (
                    <div className="py-12 text-center bg-slate-50/50 rounded-2xl border-2 border-dashed border-slate-100">
                        <Paperclip size={32} className="mx-auto text-slate-200 mb-2" />
                        <p className="text-xs font-bold text-slate-400">등록된 자료 파일이 없습니다.</p>
                    </div>
                )
            ) : (
                <div className="animate-in fade-in zoom-in-95 duration-300">
                    <div className="flex items-center justify-between mb-4">
                        <button
                            onClick={() => setSelectedFileSetId(null)}
                            className="flex items-center gap-1.5 text-[10px] font-black text-slate-400 hover:text-slate-600 transition-colors"
                        >
                            <RotateCcw size={12} /> 목록으로 돌아가기
                        </button>
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-black text-slate-800">{fileSets.find(s => s.id === selectedFileSetId)?.title}</span>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {fileSets.find(s => s.id === selectedFileSetId)?.files.map((file: any, idx: number) => (
                            <a
                                key={idx} href={file.url} target="_blank" rel="noreferrer"
                                className="flex items-center justify-between p-3 bg-slate-50 rounded-2xl border border-transparent hover:border-emerald-200 hover:bg-white transition-all group"
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className={`w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center transition-all shrink-0 ${file.category === 'pdf' ? 'text-rose-500 group-hover:bg-rose-500 group-hover:text-white' : 'text-amber-500 group-hover:bg-amber-500 group-hover:text-white'}`}>
                                        {file.category === 'pdf' ? <FileText size={18} /> : <Music size={18} />}
                                    </div>
                                    <div className="min-w-0">
                                        <h5 className="text-xs font-black text-slate-800 truncate">{file.title}</h5>
                                        <p className="text-[9px] text-slate-400 font-bold">{formatSize(file.size)} • {file.category.toUpperCase()}</p>
                                    </div>
                                </div>
                                <Download size={14} className="text-slate-300 group-hover:text-emerald-500 ml-2 shrink-0" />
                            </a>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ScheduleBookFile;
